class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.isConnecting = false;
  }

  connect(url = 'ws://localhost:8001/ws/flow') {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          // Only log if we were previously connected (not initial connection failure)
          if (this.reconnectAttempts > 0) {
            console.debug('WebSocket disconnected:', event.code, event.reason);
          }
          this.isConnecting = false;
          this.handleReconnect();
        };

        this.ws.onerror = (error) => {
          // Don't log errors on initial connection if backend is offline (expected)
          if (this.reconnectAttempts === 0) {
            // Silently fail initial connection - backend may not be running
            this.isConnecting = false;
            reject(error);
          } else {
            console.debug('WebSocket error (reconnection attempt):', error);
            this.isConnecting = false;
            reject(error);
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  handleMessage(data) {
    const { type } = data;
    if (this.listeners.has(type)) {
      this.listeners.get(type).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket message handler:', error);
        }
      });
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      // Only log after first few attempts to reduce noise
      if (this.reconnectAttempts <= 2) {
        console.debug(`WebSocket: Backend unavailable (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      }
      
      setTimeout(() => {
        this.connect().catch(() => {
          // Silently handle reconnection failures
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      // Only log once when max attempts reached
      if (this.reconnectAttempts === this.maxReconnectAttempts) {
        console.debug('WebSocket: Backend unavailable. Features requiring backend will use fallbacks.');
      }
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    } else {
      // Silently attempt to connect - don't spam console if backend is offline
      this.connect().then(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(data));
        }
      }).catch(() => {
        // Silently handle - backend may not be running
      });
      return false;
    }
  }

  on(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push(callback);
  }

  off(type, callback) {
    if (this.listeners.has(type)) {
      const callbacks = this.listeners.get(type);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  executeFlow(flow, executionId) {
    return this.send({
      type: 'execute_flow',
      flow,
      execution_id: executionId
    });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.listeners.clear();
  }
}

export default new WebSocketService();
